
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { checkManagerAuth } from "@/utils/managerAuth";

interface NewsItem {
  id: string;
  title: string;
  content: string;
  created_at: string;
}

interface MemberData {
  id: string;
  organization_id: string;
  role: string;
  full_name: string;
  organization: {
    id: string;
    name: string;
  };
}

export const useDashboardData = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState<MemberData | null>(null);
  const [news, setNews] = useState<NewsItem[]>([]);

  // List of public routes that don't need auth
  const publicRoutes = ['/', '/auth', '/forgot-password', '/reset-password'];
  const isPublicRoute = publicRoutes.includes(location.pathname) || location.search.includes('invited=true');

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        // Skip auth checks for public routes
        if (isPublicRoute) {
          setLoading(false);
          return;
        }

        const isAuthorized = await checkManagerAuth();
        if (!isAuthorized) {
          console.error("Manager auth check failed");
          navigate("/auth");
          return;
        }

        const memberId = sessionStorage.getItem('organization_member_id');
        if (!memberId) {
          throw new Error("No member ID found");
        }

        const [memberResult, sessionResult] = await Promise.all([
          supabase
            .from('organization_members')
            .select('*, organization:organizations(*)')
            .eq('id', memberId)
            .maybeSingle(),
          supabase.auth.getSession()
        ]);

        if (memberResult.error) throw memberResult.error;
        if (!memberResult.data) throw new Error("Member not found");
        if (!sessionResult.data.session) throw new Error("No active session");

        sessionStorage.setItem('organization_id', memberResult.data.organization_id);
        setMemberData(memberResult.data);

        const { data: newsData, error: newsError } = await supabase
          .from('organization_news')
          .select('*')
          .eq('organization_id', memberResult.data.organization_id)
          .order('created_at', { ascending: false })
          .limit(5);

        if (newsError) throw newsError;
        setNews(newsData || []);

      } catch (error: any) {
        // Only show errors and redirect for non-public routes
        if (!isPublicRoute) {
          console.error("Dashboard initialization error:", error);
          toast.error(error.message || "Failed to load dashboard");
          navigate("/auth");
        }
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, [navigate, location.pathname, isPublicRoute]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      sessionStorage.removeItem('organization_member_id');
      sessionStorage.removeItem('organization_id');
      navigate("/auth");
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Failed to sign out");
    }
  };

  return {
    loading,
    memberData,
    news,
    handleSignOut
  };
};
